import React, {useCallback, useMemo} from 'react'
import {
    Box,
    Flex,
    Image,
    Link as NavLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'
import {getUrl} from '../../utils/cloudinary-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import FindStore from './find-store'
import {useSiteContext} from '../../contexts/site'
import {KS_MY_ACCOUNT_URL, KS_WISHLIST_URL} from '../../constants'
import {useGlobaleContext} from '../../contexts/globale'
import useTagManager from '../../hooks/use-tag-manager'
import {useWishList} from '../../hooks/use-wish-list'
import {scheduleIdleAnalytics} from '../../utils/common-utils'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'

const UtilityBanner = () => {
    const {isYR} = useSiteContext()
    const {
        app: {commerceAPI}
    } = getConfig()

    const {siteTheme} = commerceAPI.storefront

    const themeJSON = useMemo(() => {
        try {
            return JSON.parse(siteTheme)
        } catch (e) {
            return {}
        }
    }, [siteTheme])

    const tagManager = useTagManager()
    const {countryISO} = useGlobaleContext()
    const {data: wishListData} = useWishList()
    const handleNavigation = useNavigationHandler()

    const trackHeaderInteraction = useCallback(
        (action) => {
            scheduleIdleAnalytics(() => {
                tagManager.headerInteraction(action)
            })
        },
        [tagManager]
    )

    const onKsLogoClick = useCallback(
        (event) => {
            handleNavigation(event, getNavigationProps('/'), () => {
                trackHeaderInteraction('kendra-scott')
            })
        },
        [handleNavigation, trackHeaderInteraction]
    )

    const onYrLogoClick = useCallback(
        (event) => {
            handleNavigation(event, getNavigationProps('/yellow-rose.html'), () => {
                trackHeaderInteraction('yellow-rose')
            })
        },
        [handleNavigation, trackHeaderInteraction]
    )

    return (
        <>
            <Flex
                className="utility-banner-wrapper"
                height={{base: '40px', lg: '48px'}}
                justify="space-between"
                align="center"
                p={{base: 0, lg: '0 60px'}}
                bg={
                    isYR
                        ? themeJSON['yr']?.brandBarBg || 'black.300'
                        : themeJSON['ks']?.brandBarBg || 'gray.50'
                }
                color={isYR ? 'white' : 'black.300'}
                fontFamily="body"
                fontSize="12px"
                fontWeight="300"
                transition="background-color 180ms ease, color 180ms ease"
                sx={{
                    '.utility-link': {
                        transition: 'background-color 180ms ease'
                    },
                    '.utility-link:hover, .utility-link:focus-visible': {
                        backgroundColor: isYR ? 'grayAlpha5' : 'gray.100'
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&, .brand-logo, .brand-logo img, .utility-link': {transition: 'none'}
                    }
                }}
            >
                {/* Left Side */}
                <Flex
                    className="left-logos"
                    gap="0px"
                    alignItems="center"
                    height="100%"
                    width={{base: '100%', lg: 'auto'}}
                >
                    <Box
                        className="brand-logo ks-logo"
                        height="100%"
                        width={{base: '50%', lg: 'auto'}}
                        bg={isYR ? {base: 'yrInactiveKsBrandGradient', lg: 'transparent'} : 'white'}
                        display="flex"
                        justify="center"
                        align="center"
                        overflow="hidden"
                        transition="background-color 180ms ease"
                    >
                        <Link
                            to="/"
                            useNavLink
                            className="ks-link"
                            aria-label="Kendra Scott homepage"
                            padding={{base: 0, lg: '0 5px'}}
                            alignContent="center"
                            justifyContent="center"
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            _hover={{bg: isYR ? 'black.200' : 'grayAlpha5'}}
                            onClick={onKsLogoClick}
                        >
                            <Box position="relative" width="118px" height="8px" flex="none">
                                <Image
                                    src={getUrl('UIImages/images/black-ks-utility.svg')}
                                    position="absolute"
                                    width="118px"
                                    height="8px"
                                    alt=""
                                    aria-hidden="true"
                                    opacity={isYR ? 0 : 1}
                                    transition="opacity 180ms ease"
                                />
                                <Image
                                    src={getUrl('UIImages/images/white-ks-utility.svg')}
                                    position="absolute"
                                    width="118px"
                                    height="8px"
                                    alt=""
                                    aria-hidden="true"
                                    opacity={isYR ? 1 : 0}
                                    transition="opacity 180ms ease"
                                />
                            </Box>
                        </Link>
                    </Box>
                    <Box
                        className="brand-logo redirect-link yr-logo"
                        height="100%"
                        width={{base: '50%', lg: 'auto'}}
                        bg={isYR ? {base: 'black.300', lg: 'white'} : 'gray.50'}
                        display="flex"
                        justify="center"
                        align="center"
                        overflow="hidden"
                        transition="background-color 180ms ease"
                    >
                        <Link
                            to="/yellow-rose.html"
                            useNavLink
                            className="yr-link"
                            aria-label="Yellow Rose homepage"
                            padding={{base: 0, lg: '0 25px'}}
                            width="100%"
                            height="100%"
                            alignContent="center"
                            justifyContent="center"
                            alignItems="center"
                            display="flex"
                            _hover={{bg: isYR ? {base: 'black.200', lg: 'gray.100'} : 'gray.100'}}
                            onClick={onYrLogoClick}
                        >
                            <Box position="relative" width="76.62px" height="11.81px" flex="none">
                                <Image
                                    src={getUrl('UIImages/images/black-yr-utility.svg')}
                                    position="absolute"
                                    width="76.62px"
                                    height="11.81px"
                                    alt=""
                                    aria-hidden="true"
                                    opacity={{base: isYR ? 0 : 1, lg: 1}}
                                    transition="opacity 180ms ease"
                                />
                                <Image
                                    src={getUrl('UIImages/images/white-yr-utility.svg')}
                                    position="absolute"
                                    width="76.62px"
                                    height="11.81px"
                                    alt=""
                                    aria-hidden="true"
                                    opacity={{base: isYR ? 1 : 0, lg: 0}}
                                    transition="opacity 180ms ease"
                                />
                            </Box>
                        </Link>
                    </Box>
                </Flex>

                {/* Right Side */}
                <Flex
                    className="right-content"
                    align="center"
                    ml="auto"
                    display={{base: 'none', lg: 'block'}}
                >
                    <Flex className="utility-banner-svgs" align="center">
                        <Link
                            className="utility-link my-account-wrapper"
                            to={KS_MY_ACCOUNT_URL}
                            isExternal={true}
                            as={NavLink}
                            p="10px"
                            my="7.5px"
                            borderRadius="5px"
                            onClick={() => {
                                trackHeaderInteraction('account')
                            }}
                        >
                            <Image
                                width="16px"
                                height="16px"
                                src={getUrl(
                                    isYR
                                        ? 'UIImages/images/user-circle-white.svg'
                                        : 'UIImages/images/user-circle-black.svg'
                                )}
                                alt="My Account"
                            />
                        </Link>

                        {countryISO === 'US' && (
                            <Link
                                className="utility-link wishlist-wrapper"
                                to={KS_WISHLIST_URL}
                                isExternal={true}
                                data-wishlist-count={wishListData?.customerProductListItems?.length}
                                as={NavLink}
                                p="10px"
                                my="7.5px"
                                borderRadius="5px"
                                onClick={() => {
                                    trackHeaderInteraction('wishlist')
                                }}
                            >
                                <Image
                                    width="16px"
                                    height="16px"
                                    src={getUrl(
                                        isYR
                                            ? 'UIImages/images/heart-white.svg'
                                            : 'UIImages/images/heart-black.svg'
                                    )}
                                    alt="Wishlist"
                                />
                            </Link>
                        )}

                        <FindStore />
                    </Flex>
                </Flex>
            </Flex>
        </>
    )
}

export default UtilityBanner
